import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { SchemaProviderInstance } from "../@shared/services/SchemaProvider";
import { onThrowAlert, loadOpenedSchema } from "../sagas/dataSaga/dataSagaActions";
import { GuideStepEnum, NotificationModel, NotificationModelType } from "../reducers/main/mainModel";
import { getModelId, getModelName, getModelDescription, getSchemaItems, cleanSchema, setIsShowSchemaModal, setSchemaItems } from "../reducers/schema/schemaReducer";
import { CreateSchema, SchemaItem, StructureItemType } from "../@shared/Models/Models";
import { setIsSchemaWasRemoved, setOpenedSchema } from "../reducers/data/dataReducer";
import { useLeftPanel } from "./useLeftPanel";
import { getGuideStep, setGuideStep } from "../reducers/main/mainReducer";

export type UseSchemaResult = {
    onSaveSchema: (isForce: boolean) => Promise<void>;
    onRemoveSchema: (id: string) => Promise<void>;
    onAddListOfSchemaItems: (items: SchemaItem[]) => void;
    isProcessing: boolean;
}

export const useSchema = (): UseSchemaResult => {
    const dispatch = useDispatch();
    const id = useSelector(getModelId);
    const name = useSelector(getModelName);
    const description = useSelector(getModelDescription);
    const structure = useSelector(getSchemaItems);
    const [isProcessing, setIsProcessing] = useState(false);
    const guideStep = useSelector(getGuideStep);

    const { onLoadWithCleaning } = useLeftPanel();

    const onSaveSchema = useCallback(async (isForce: boolean) => {
        try {
            // Validate Category items have at least 2 values
            const invalidCategoryItems = structure.filter(
                item => item.valueTypeId === StructureItemType.Category && 
                (!item.categories || item.categories.length < 2)
            );

            if (invalidCategoryItems.length > 0) {
                const itemNames = invalidCategoryItems.map(i => i.name || 'unnamed').join(', ');
                dispatch(onThrowAlert({
                    message: `Category items must have at least 2 values: ${itemNames}`,
                    lifeTime: 10000
                } as NotificationModel));
                return;
            }

            setIsProcessing(true);
            const data = { name: name, schemaItems: structure, description: description } as CreateSchema;
            if (!!id) {
                data.id = id;
            }
            const result = await SchemaProviderInstance.create(data, isForce);
            if (result.status !== 200) {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                dispatch(setIsShowSchemaModal(false));
                dispatch(cleanSchema());
                onLoadWithCleaning();
                if (!!id) {
                    dispatch(loadOpenedSchema(id));
                }
                dispatch(onThrowAlert({
                    message: "Schema successfully saved",
                    lifeTime: 3000,
                    type: NotificationModelType.Success
                } as NotificationModel));

                if(guideStep !== GuideStepEnum.None){
                    dispatch(setGuideStep(GuideStepEnum.OpenCreatedSchema));
                }
            }

        }
        catch (error) {
            dispatch(onThrowAlert({
                message: (error as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsProcessing(false);
        }

    }, [description, dispatch, guideStep, id, name, onLoadWithCleaning, structure])

    const onRemoveSchema = useCallback(async (id: string) => {
        try {
            setIsProcessing(true);
            const result = await SchemaProviderInstance.remove(id);
            if (result.status !== 200) {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                dispatch(setIsSchemaWasRemoved(true));
                dispatch(setOpenedSchema(undefined));
                onLoadWithCleaning();
                dispatch(onThrowAlert({
                    message: "Schema successfully removed",
                    lifeTime: 3000,
                    type: NotificationModelType.Warning
                } as NotificationModel));
            }
        }
        catch (error) {
            dispatch(onThrowAlert({
                message: (error as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsProcessing(false);
        }

    }, [dispatch, onLoadWithCleaning])

    const onAddListOfSchemaItems = useCallback((items: SchemaItem[]) => {
        dispatch(setSchemaItems(items));
    }, [dispatch]);

    return {
        onSaveSchema,
        onRemoveSchema,
        isProcessing,
        onAddListOfSchemaItems
    }
}